/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminPortfolioShow from 'src/components/Admin/AdminPortfolio/AdminPortfolioShow';
import AdminShowPlaceholder from 'src/components/Admin/AdminShowPlaceholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getShowRouteKey from './helpers/getShowRouteKey';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_portfolio.admin_portfolio_show.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading this portfolio. Please try again.',
  },
  notFoundTitle: {
    id: 'admin.admin_portfolio.admin_portfolio_show.page.not_found.title',
    defaultMessage: 'Portfolio not found',
  },
});

export const adminPortfolioShowRouteQuery = graphql`
  query AdminPortfolioShowRoute_Query($uuid: ID!) {
    portfolio(uuid: $uuid) {
      ...AdminPortfolioShow_portfolio
    }
  }
`;

/**
 * Renders `AdminPortfolioShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin portfolio show route content.
 */
function AdminPortfolioShowPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminPortfolioShowRouteQuery, queryRef);

  return <AdminPortfolioShow {...routeProps} {...queryData} />;
}

AdminPortfolioShowPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminPortfolioShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin portfolio show route content.
 */
function AdminPortfolioShowRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminPortfolioShowRouteQuery,
    routeProps,
    { logLabel: 'AdminPortfolioShowRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      notFound={{ title: intl.formatMessage(messages.notFoundTitle), backPath: '/admin/portfolios' }}
      placeholderComponent={AdminShowPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminPortfolioShowPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminPortfolioShow {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminPortfolioShowRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the AdminPortfolioShow route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. The renderer is keyed with `getShowRouteKey`
 * (uuid and non-locale query params) so navigation remounts it and drops a retried
 * query reference that would otherwise mask a later route error. Authentication is resolved by the
 * owning `AdminLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} AdminPortfolioShow route content or loading state.
 */
export function adminPortfolioShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <AdminPortfolioShowRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <AdminPortfolioShowRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default AdminPortfolioShowRouteRenderer;
